import {
  renderDonutForPdf,
  renderBarForPdf,
  renderDualAxesForPdf,
  drawChartRow,
  drawChartSection,
} from "../pdfCharts";
import { formatWhole } from "../../../../utils/numberFormat";
import { BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import {
  fetchProductionAnnualTotals,
  buildProductionData,
  buildWorkersData,
  buildGenderData,
  GENDER_TYPE_LABEL_KEYS,
} from "./TableGraf";
import {
  fetchProductionMonthlyData,
  buildProductionComparisonData,
  buildWorkersComparisonData,
  buildProductivityData,
} from "./socialChartData";

// "Enero" -> "Ene", etc. — con los gráficos compartiendo página el nombre
// completo del mes no entra sin superponerse (mismo ajuste que en
// Energía/Combustibles/Agua/Efluentes).
const monthLabel3 = (v) => String(v).slice(0, 3);

function buildSharedYScale(a, b) {
  const combinedMax = Math.max(0, ...a.map((d) => d.value || 0), ...b.map((d) => d.value || 0));
  const domainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  return { domainMin: 0, domainMax, nice: true };
}

function buildBarLineChildren(barData, lineData, yearColorMap, sharedYScale) {
  return [
    {
      data: barData,
      type: "interval",
      yField: "value",
      colorField: "year",
      group: true,
      style: { maxWidth: 40 },
      scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
    },
    {
      data: lineData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
    },
  ];
}

function buildLineLineChildren(consumptionData, lineData, sharedYScale) {
  return [
    {
      data: consumptionData,
      type: "line",
      yField: "value",
      colorField: "year",
      style: { lineWidth: 4 },
      scale: { y: sharedYScale },
    },
    {
      data: lineData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
    },
  ];
}

async function renderMonthlyDualAxesForPdf(children, yearColorMap) {
  const years = Object.keys(yearColorMap);
  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: {
      color: { domain: years, range: years.map((y) => yearColorMap[y]) },
      x: { paddingOuter: 0.1 },
    },
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: false, grid: true, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatWhole(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems };
}

// "Comparación producción"/"Comparación trabajadores" — barra mensual por
// año + línea de promedio punteada, misma config que
// SocialComparisonChart/SocialMonthlyChart.
async function renderBarLineForPdf(barData, lineData, yearColorMap) {
  const sharedYScale = buildSharedYScale(barData, lineData);
  const children = buildBarLineChildren(barData, lineData, yearColorMap, sharedYScale);
  return renderMonthlyDualAxesForPdf(children, yearColorMap);
}

// "Comparación productividad" — a diferencia de los otros 2, sin barra: 2
// líneas (productividad real sólida + promedio punteado), misma config que
// ProductivityChart.
async function renderLineLineForPdf(consumptionData, lineData, yearColorMap) {
  const sharedYScale = buildSharedYScale(consumptionData, lineData);
  const children = buildLineLineChildren(consumptionData, lineData, sharedYScale);
  return renderMonthlyDualAxesForPdf(children, yearColorMap);
}

// Las 3 tarjetas de TableGraf.js (barra Producción + barra Trabajadores +
// dona Género) como SVG vectorial — misma lógica de datos que ya usa
// TableGraf.js en pantalla. La barra de Producción necesita su propio
// fetch anual (fetchProductionAnnualTotals), igual que hace TableGraf.js
// en su useEffect.
export async function renderSocialTableGrafForPdf(totales, filters, { productionController, accessToken, site, baseline, t }) {
  const productionValues = await fetchProductionAnnualTotals(productionController, accessToken, site, baseline);
  const [barProduccion, barTrabajadores, donutGenero] = await Promise.all([
    // Regla 1 (AJUSTES DE MAGNITUDES NUMÉRICAS): espacio fino separador de
    // miles, sin decimales — mismo criterio que ya tienen las 3 secciones
    // mensuales de más abajo (renderMonthlyDualAxesForPdf).
    renderBarForPdf(buildProductionData(baseline, productionValues), t("total_production_axis"), BAR_PALETTE, { yFormatter: formatWhole }),
    renderBarForPdf(buildWorkersData(totales), t("total_workers_axis"), BAR_PALETTE, { yFormatter: formatWhole }),
    renderDonutForPdf(
      buildGenderData(totales, filters).map((d) => ({ ...d, label: t(GENDER_TYPE_LABEL_KEYS[d.type] || d.type) })),
      DONUT_COLORS.social,
    ),
  ]);
  return { barProduccion, barTrabajadores, donutGenero };
}

// Orquesta el dibujado de las 3 tarjetas de TableGraf.js + las 3 secciones
// mensuales (Producción/Trabajadores/Productividad) en el PDF: se pasa
// como `renderCustomCharts` a KPIExportIconButton. Mismo criterio de
// layout ya resuelto en Energía/Combustibles/Agua/Efluentes/Residuos: fila
// de 3 tarjetas con altura fija, después las secciones DualAxes de a 2 por
// hoja, con techo de altura para no ocupar toda la hoja si queda sola.
export async function drawSocialCustomCharts(doc, ctx, {
  totales,
  filters,
  baseline,
  resultsByYear,
  resultsWorkers,
  resultsProductions,
  productionController,
  accessToken,
  site,
  t,
}) {
  const { margin, pageWidth, pageHeight, imgWidth } = ctx;
  let yOffset = ctx.yOffset;

  const { barProduccion, barTrabajadores, donutGenero } = await renderSocialTableGrafForPdf(totales, filters, {
    productionController,
    accessToken,
    site,
    baseline,
    t,
  });
  const cards = [
    { title: t("production_last_5_years"), ...barProduccion },
    { title: t("workers_last_5_years"), ...barTrabajadores },
    { title: t("gender_distribution"), ...donutGenero },
  ];
  const CHART_ROW_H = 210;
  if (CHART_ROW_H > pageHeight - margin - yOffset) {
    doc.addPage();
    yOffset = margin;
  }
  await drawChartRow(doc, cards, { x: margin, y: yOffset, availW: imgWidth, cardH: CHART_ROW_H, emptyMessage: t("no_data_available") });
  yOffset += CHART_ROW_H + 10;
  barProduccion.cleanup?.();
  barTrabajadores.cleanup?.();
  donutGenero.cleanup?.();

  const monthlyProductionData = await fetchProductionMonthlyData(productionController, accessToken, site, baseline);

  const GAP = 10;
  const PER_PAGE = 2;
  const MIN_EACH_H = 240;
  const MAX_EACH_H = 320;
  const sections = [
    {
      title: t("production_comparison_last_3_years"),
      render: () => {
        const { barData, lineData, yearColorMap } = buildProductionComparisonData(monthlyProductionData, baseline, t);
        return renderBarLineForPdf(barData, lineData, yearColorMap);
      },
    },
    {
      title: t("workers_comparison_last_3_years"),
      render: () => {
        const { barData, lineData, yearColorMap } = buildWorkersComparisonData(resultsByYear, t);
        return renderBarLineForPdf(barData, lineData, yearColorMap);
      },
    },
    {
      title: t("productivity_comparison_last_3_years"),
      render: () => {
        const { consumptionData, lineData, yearColorMap } = buildProductivityData(baseline, resultsWorkers, resultsProductions, t);
        return renderLineLineForPdf(consumptionData, lineData, yearColorMap);
      },
    },
  ];

  for (let i = 0; i < sections.length; i += PER_PAGE) {
    const group = sections.slice(i, i + PER_PAGE);
    let availForGroup = pageHeight - margin - yOffset - GAP * (group.length - 1);
    if (availForGroup < MIN_EACH_H * group.length) {
      doc.addPage();
      yOffset = margin;
      availForGroup = pageHeight - margin - yOffset - GAP * (group.length - 1);
    }
    const sectionH = Math.min(Math.floor(availForGroup / group.length), MAX_EACH_H);

    for (const section of group) {
      const result = await section.render();
      await drawChartSection(doc, { x: margin, y: yOffset, width: imgWidth, height: sectionH, title: section.title, emptyMessage: t("no_data_available"), ...result });
      yOffset += sectionH + GAP;
      result.cleanup?.();
    }
  }

  return yOffset;
}
